/* ═══════════════════════════════════════════════════════════════════════════
 * POSTES ET FICHES DE POSTE — les trois gestes
 * ═══════════════════════════════════════════════════════════════════════════
 * Cet ecran editait un ORGANIGRAMME (titre, departement, salaire min/max,
 * poste superieur) et ne touchait ni au CODE que porte `agents.poste`, ni aux
 * fiches, ni aux periodes d'essai. Reecrit le 05/09/2026 pour faire les trois
 * gestes : CREER un poste · EDITER sa fiche · L'IMPRIMER.
 *
 * ⚠️ UN TENANT A SES PROPRES POSTES. Aucune liste en dur ici : elle vient de
 *    `/postes`. VIGIE porte `agent_jour`, `agent_nuit`, `rondier`... aucun ne
 *    figurait dans la liste de six ecrite dans le code avant ce lot.
 *
 * ⚠️ LES ALERTES DE PERIODE D'ESSAI VIENNENT DU SERVEUR, jamais recalculees
 *    ici : une regle qui vit a deux endroits diverge au premier changement de
 *    texte. L'ecran AFFICHE le verdict, il ne le produit pas.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const POSTE_VIDE = {
  code: '', titre: '', departement: '', niveau: '',
  duree_essai_initiale: '', duree_essai_unite: 'mois', duree_essai_renouvellement: '',
  cran_propose: '', description: '',
};
const FICHE_VIDE = {
  intitule: '', rattachement_hierarchique: '', missions: '', responsabilites: '',
  competences_requises: '', conditions_exercice: '', equipements_fournis: '',
};

/** `competences_requises` est un jsonb : tableau, objet ou texte selon l'age de la ligne. */
function competencesEnTexte(v) {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join('\n');
  if (typeof v === 'object') return Object.entries(v).map(([k, x]) => `${k} : ${x}`).join('\n');
  return String(v);
}

export default function PostesPage() {
  const [postes, setPostes] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [pays, setPays] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [showPoste, setShowPoste] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formPoste, setFormPoste] = useState(POSTE_VIDE);

  const [showFiche, setShowFiche] = useState(false);
  const [posteFiche, setPosteFiche] = useState(null);
  const [formFiche, setFormFiche] = useState(FICHE_VIDE);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEscapeClose(showPoste, () => setShowPoste(false));
  useEscapeClose(showFiche, () => setShowFiche(false));

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/postes');
      setPostes(data.postes || []);
      setNiveaux(data.niveaux || []);
      setPays(data.pays || '');
    } catch (e) {
      setError(e.response?.data?.error || 'Chargement impossible.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // ── POSTE ────────────────────────────────────────────────────────────────
  const ouvrirCreation = () => {
    setEditId(null); setFormPoste(POSTE_VIDE); setError(''); setShowPoste(true);
  };
  const ouvrirEdition = (p) => {
    setEditId(p.id);
    setFormPoste({
      code: p.code, titre: p.titre || '', departement: p.departement || '',
      niveau: p.niveau || '',
      duree_essai_initiale: p.duree_essai_initiale ?? '',
      duree_essai_unite: p.duree_essai_unite || 'mois',
      duree_essai_renouvellement: p.duree_essai_renouvellement ?? '',
      cran_propose: p.cran_propose || '', description: p.description || '',
    });
    setError(''); setShowPoste(true);
  };
  const enregistrerPoste = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const corps = {
        ...formPoste,
        duree_essai_initiale: formPoste.duree_essai_initiale === '' ? null : Number(formPoste.duree_essai_initiale),
        duree_essai_renouvellement: formPoste.duree_essai_renouvellement === '' ? null : Number(formPoste.duree_essai_renouvellement),
      };
      if (editId) await api.put(`/postes/${editId}`, corps);
      else await api.post('/postes', corps);
      setShowPoste(false); charger();
    } catch (err) {
      setError(err.response?.data?.error || 'Enregistrement impossible.');
    } finally { setSaving(false); }
  };
  const supprimerPoste = async (p) => {
    setError(''); setInfo('');
    try {
      await api.delete(`/postes/${p.id}`);
      setInfo(`Poste « ${p.titre} » retiré.`);
      charger();
    } catch (err) {
      // ⚠️ Le refus du serveur porte le NOMBRE d'agents rattachés : on l'affiche
      //    tel quel. Le reformuler ici ferait deux messages pour un refus.
      setError(err.response?.data?.error || 'Suppression impossible.');
    }
  };

  // ── FICHE ────────────────────────────────────────────────────────────────
  const ouvrirFiche = async (p) => {
    setPosteFiche(p); setError(''); setShowFiche(true);
    setFormFiche(FICHE_VIDE); setVersions([]);
    try {
      const { data } = await api.get(`/postes/${p.code}/fiche`);
      setVersions(data.versions || []);
      if (data.fiche) {
        setFormFiche({
          intitule: data.fiche.intitule || '',
          rattachement_hierarchique: data.fiche.rattachement_hierarchique || '',
          missions: data.fiche.missions || '',
          responsabilites: data.fiche.responsabilites || '',
          competences_requises: competencesEnTexte(data.fiche.competences_requises),
          conditions_exercice: data.fiche.conditions_exercice || '',
          equipements_fournis: data.fiche.equipements_fournis || '',
        });
      } else {
        setFormFiche({ ...FICHE_VIDE, intitule: p.titre });
      }
    } catch (e) { setError(e.response?.data?.error || 'Fiche illisible.'); }
  };
  const enregistrerFiche = async (e) => {
    e.preventDefault(); setSaving(true); setError(''); setInfo('');
    try {
      const corps = {
        ...formFiche,
        // Une ligne par compétence : c'est ainsi que le document les imprime.
        competences_requises: formFiche.competences_requises
          ? formFiche.competences_requises.split('\n').map(s => s.trim()).filter(Boolean)
          : null,
      };
      const { data } = await api.put(`/postes/${posteFiche.code}/fiche`, corps);
      setInfo(`Fiche enregistrée en version ${data.version}.`
        + (data.cloturee ? ' La version précédente reste consultable.' : ''));
      setShowFiche(false); charger();
    } catch (err) {
      setError(err.response?.data?.error || 'Enregistrement impossible.');
    } finally { setSaving(false); }
  };

  const imprimer = async (p) => {
    setError(''); setInfo('');
    try {
      const { data } = await api.post(`/postes/${p.code}/fiche/pdf`, {});
      setInfo(`Fiche « ${p.titre} » version ${data.version} produite et déposée.`);
      charger();
    } catch (err) {
      // Le refus « aucune fiche » nomme le poste : on l'affiche tel quel.
      setError(err.response?.data?.error || 'Impression impossible.');
    }
  };

  // ── Le verdict de plafond, rendu tel que le serveur l'a produit ──────────
  const Verdicts = ({ p }) => {
    const v = p.essai_verdicts || {};
    const rien = ['CDI', 'CDD'].every(t => !v[t] || (v[t].verifiable && v[t].alertes.length === 0));
    // ⚠️ « non vérifiable » n'est PAS « conforme ». Sans niveau, ou sans barème
    //    pour le pays, on le DIT — laisser la case vide ferait croire que la
    //    saisie a été contrôlée.
    const nonVerifiable = ['CDI', 'CDD'].some(t => v[t] && !v[t].verifiable);
    if (nonVerifiable) {
      return (
        <span style={{ color: '#B45309', fontSize: 12 }}>
          {p.niveau
            ? `Aucun plafond légal au référentiel pour ${pays} — durée non vérifiée.`
            : 'Niveau non renseigné — le plafond légal ne peut pas être vérifié.'}
        </span>
      );
    }
    if (rien) return <span style={{ color: '#146B43', fontSize: 12 }}>Conforme (CDI et CDD)</span>;
    return (
      <ul style={{ margin: 0, paddingLeft: 16, color: '#991B1B', fontSize: 12 }}>
        {['CDI', 'CDD'].flatMap(t => (v[t]?.alertes || []).map((a, i) => (
          <li key={t + i}><strong>{t}</strong> — {a.message}</li>
        )))}
      </ul>
    );
  };

  return (
    <div>
      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6, marginBottom: 16 }}>{error}</div>}
      {info && <div style={{ background: '#DCFCE7', color: '#146B43', padding: 12, borderRadius: 6, marginBottom: 16 }}>{info}</div>}

      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
            Postes et fiches de poste
          </div>
          <button className="gss-btn gss-btn-primary" onClick={ouvrirCreation}>+ Nouveau poste</button>
        </div>

        {loading ? <p style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</p> : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Poste</th><th>Code</th><th>Niveau</th><th>Période d’essai</th>
                  <th>Agents</th><th>Fiche</th><th></th>
                </tr>
              </thead>
              <tbody>
                {postes.map(p => (
                  <tr key={p.id}>
                    <td>{p.titre}<div style={{ fontSize: 11, color: '#777' }}>{p.departement || ''}</div></td>
                    <td><code>{p.code}</code></td>
                    <td>{p.niveau
                      ? (niveaux.find(n => n.code === p.niveau)?.libelle || p.niveau)
                      : <span style={{ color: '#B45309' }}>non renseigné</span>}</td>
                    <td>
                      {p.duree_essai_initiale
                        ? `${p.duree_essai_initiale} ${p.duree_essai_unite}(s)`
                          + (p.duree_essai_renouvellement ? ` + ${p.duree_essai_renouvellement}` : '')
                        : '—'}
                      <div style={{ marginTop: 4 }}><Verdicts p={p} /></div>
                    </td>
                    <td>{p.agents_rattaches}</td>
                    <td>{p.fiches_en_vigueur > 0
                      ? <span style={{ color: '#146B43' }}>oui</span>
                      : <span style={{ color: '#B45309' }}>aucune</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="gss-btn" onClick={() => ouvrirEdition(p)}>Modifier</button>{' '}
                      <button className="gss-btn" onClick={() => ouvrirFiche(p)}>Fiche</button>{' '}
                      {/* Le bouton d'impression n'est rendu que si une fiche existe :
                          un bouton qui échoue est pire qu'un bouton absent. */}
                      {p.fiches_en_vigueur > 0 && (
                        <button className="gss-btn" onClick={() => imprimer(p)}>Imprimer</button>
                      )}{' '}
                      <button className="gss-btn" onClick={() => supprimerPoste(p)}>Retirer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODALE POSTE ─────────────────────────────────────────────────── */}
      {showPoste && (
        <div className="gss-modal-overlay" onClick={() => setShowPoste(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>{editId ? 'Modifier le poste' : 'Nouveau poste'}</div>
              <button className="gss-modal-close" onClick={() => setShowPoste(false)}>&times;</button>
            </div>
            <form onSubmit={enregistrerPoste}>
              <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
                <div className="gss-field">
                  <label>Code du poste *</label>
                  {/* ⚠️ Le code ne se modifie pas : il est porté par la fiche de
                      chaque agent, sous contrainte. Le renommer romprait le lien. */}
                  <input className="gss-input" required disabled={!!editId}
                    value={formPoste.code}
                    onChange={e => setFormPoste(f => ({ ...f, code: e.target.value }))} />
                  {!editId && <small style={{ color: '#555' }}>
                    Minuscules, chiffres et tirets bas. C’est ce code que porteront les fiches du personnel.
                  </small>}
                </div>
                <div className="gss-field">
                  <label>Intitulé *</label>
                  <input className="gss-input" required value={formPoste.titre}
                    onChange={e => setFormPoste(f => ({ ...f, titre: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Département</label>
                  <input className="gss-input" value={formPoste.departement}
                    onChange={e => setFormPoste(f => ({ ...f, departement: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Niveau (droit du travail)</label>
                  <select className="gss-input" value={formPoste.niveau}
                    onChange={e => setFormPoste(f => ({ ...f, niveau: e.target.value }))}>
                    <option value="">— non renseigné —</option>
                    {niveaux.map(n => <option key={n.code} value={n.code}>{n.libelle}</option>)}
                  </select>
                  <small style={{ color: '#555' }}>
                    C’est lui qui commande le plafond légal de période d’essai.
                  </small>
                </div>
                <div className="gss-field">
                  <label>Période d’essai — durée initiale</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="gss-input" type="number" min="1" style={{ maxWidth: 90 }}
                      value={formPoste.duree_essai_initiale}
                      onChange={e => setFormPoste(f => ({ ...f, duree_essai_initiale: e.target.value }))} />
                    <select className="gss-input" style={{ maxWidth: 110 }}
                      value={formPoste.duree_essai_unite}
                      onChange={e => setFormPoste(f => ({ ...f, duree_essai_unite: e.target.value }))}>
                      <option value="mois">mois</option>
                      <option value="jour">jours</option>
                    </select>
                  </div>
                </div>
                <div className="gss-field">
                  <label>Période d’essai — renouvellement</label>
                  <input className="gss-input" type="number" min="0"
                    value={formPoste.duree_essai_renouvellement}
                    onChange={e => setFormPoste(f => ({ ...f, duree_essai_renouvellement: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Catégorie proposée par défaut</label>
                  <input className="gss-input" value={formPoste.cran_propose}
                    onChange={e => setFormPoste(f => ({ ...f, cran_propose: e.target.value }))} />
                  <small style={{ color: '#555' }}>
                    Proposée à la saisie. Elle ne classe personne : la catégorie d’un agent
                    se décide agent par agent, avec sa date d’effet.
                  </small>
                </div>
                <div className="gss-field gss-field-full">
                  <label>Description</label>
                  <textarea className="gss-input" rows="2" value={formPoste.description}
                    onChange={e => setFormPoste(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>
              <div className="gss-modal-footer">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowPoste(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement…' : editId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODALE FICHE ─────────────────────────────────────────────────── */}
      {showFiche && posteFiche && (
        <div className="gss-modal-overlay" onClick={() => setShowFiche(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>Fiche de poste — {posteFiche.titre}</div>
              <button className="gss-modal-close" onClick={() => setShowFiche(false)}>&times;</button>
            </div>
            <form onSubmit={enregistrerFiche}>
              <div style={{ padding: '1rem 1.5rem 0', color: '#555', fontSize: 12 }}>
                {versions.length === 0
                  ? 'Aucune version : le contenu ci-dessous créera la version 1.'
                  : `Version en vigueur : ${versions[0].version}. Enregistrer créera la version ${versions[0].version + 1} ; l’actuelle reste consultable.`}
              </div>
              <div className="gss-form-grid" style={{ padding: '1rem 1.5rem 1.5rem' }}>
                <div className="gss-field">
                  <label>Intitulé sur le document</label>
                  <input className="gss-input" value={formFiche.intitule}
                    onChange={e => setFormFiche(f => ({ ...f, intitule: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Rattachement hiérarchique</label>
                  <input className="gss-input" value={formFiche.rattachement_hierarchique}
                    onChange={e => setFormFiche(f => ({ ...f, rattachement_hierarchique: e.target.value }))} />
                </div>
                {[
                  ['missions', 'Missions'],
                  ['responsabilites', 'Responsabilités'],
                  ['competences_requises', 'Compétences requises (une par ligne)'],
                  ['conditions_exercice', "Conditions d'exercice"],
                  ['equipements_fournis', 'Équipements fournis'],
                ].map(([cle, libelle]) => (
                  <div className="gss-field gss-field-full" key={cle}>
                    <label>{libelle}</label>
                    <textarea className="gss-input" rows="3" value={formFiche[cle]}
                      onChange={e => setFormFiche(f => ({ ...f, [cle]: e.target.value }))}
                      style={{ resize: 'vertical' }} />
                  </div>
                ))}
              </div>
              <div className="gss-modal-footer">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowFiche(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer la fiche'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
